import React, { useMemo, useState, useEffect } from 'react';
import { useReactTable, getCoreRowModel, flexRender, getFilteredRowModel } from '@tanstack/react-table';
import AvatarStack from './AvatarStack';

// Color mapping for action buttons
const getButtonColorClasses = (color) => {
  const colorMap = {
    blue: 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500',
    red: 'bg-red-500 hover:bg-red-600 focus:ring-red-500',
    green: 'bg-green-500 hover:bg-green-600 focus:ring-green-500',
    yellow: 'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500',
    purple: 'bg-purple-500 hover:bg-purple-600 focus:ring-purple-500',
    gray: 'bg-gray-500 hover:bg-gray-600 focus:ring-gray-500',
    indigo: 'bg-indigo-500 hover:bg-indigo-600 focus:ring-indigo-500',
  };
  return colorMap[color] || colorMap.blue; // Default to blue if color not found
};

const ReactTable = ({ headers, data, actions, searchKey = 'label', onSearch, searchDelay = 500, searchValue = '', enableRowSelection = false, bulkActions = [], pagination = null, onPageChange = null, onLimitChange = null, loading = false, sortBy = null, sortOrder = 'asc', onSort = null }) => {
  const [globalFilter, setGlobalFilter] = useState(searchValue);
  const [selectedIds, setSelectedIds] = useState([]);

  // Sync internal state with searchValue prop when it changes
  useEffect(() => {
    setGlobalFilter(searchValue);
  }, [searchValue]);

  // Handle column sorting
  const handleSort = (columnKey) => {
    if (!onSort) return;
    
    let newSortOrder = 'asc';
    if (sortBy === columnKey && sortOrder === 'asc') {
      newSortOrder = 'desc';
    }
    
    onSort(columnKey, newSortOrder);
  };

  const selectionColumn = useMemo(() => enableRowSelection ? {
    id: 'select',
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={selectedIds.length > 0 && selectedIds.length === data.length}
        indeterminate={selectedIds.length > 0 && selectedIds.length < data.length}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedIds(data.map(r => r._id));
          } else {
            setSelectedIds([]);
          }
        }}
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={selectedIds.includes(row.original._id)}
        onChange={(e) => {
          const id = row.original._id;
          setSelectedIds(prev => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
        }}
      />
    ),
  } : null, [enableRowSelection, selectedIds, data]);

  const columns = React.useMemo(
    () => [
      selectionColumn,
      ...headers.map((header) => ({
        header: onSort ? (
          <div 
            className="flex items-center cursor-pointer hover:text-gray-700 select-none group"
            onClick={() => handleSort(header.key)}
          >
            <span className="font-medium">{header.label}</span>
            <div className="ml-2 flex flex-col items-center">
              <svg 
                className={`w-3 h-3 transition-colors duration-200 ${
                  sortBy === header.key && sortOrder === 'asc' 
                    ? 'text-blue-600' 
                    : sortBy === header.key 
                      ? 'text-gray-300' 
                      : 'text-gray-400 group-hover:text-gray-500'
                }`}
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <svg 
                className={`w-3 h-3 transition-colors duration-200 ${
                  sortBy === header.key && sortOrder === 'desc' 
                    ? 'text-blue-600' 
                    : sortBy === header.key 
                      ? 'text-gray-300' 
                      : 'text-gray-400 group-hover:text-gray-500'
                }`}
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        ) : header.label,
        accessorKey: header.key,
      })),
      actions && {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={() => action.onClick(row.original._id)}
                className={`
                  inline-flex items-center justify-center
                  px-3 py-1.5 
                  text-sm font-medium text-white 
                  rounded-md
                  transition-colors duration-200
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  ${getButtonColorClasses(action.color)}
                `}
              >
                {action.label}
              </button>
            ))}
          </div>
        ),
      },
    ].filter(Boolean),
    [headers, actions, selectionColumn, onSort, sortBy, sortOrder]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: onSearch ? undefined : getFilteredRowModel(),
    state: {
      globalFilter: onSearch ? undefined : globalFilter,
    },
    onGlobalFilterChange: onSearch ? undefined : setGlobalFilter,
    globalFilterFn: onSearch ? undefined : (row, columnId, filterValue) => {
      if (!searchKey) return true;
      const searchKeys = Array.isArray(searchKey) ? searchKey : [searchKey];
      return searchKeys.some(key => {
        const value = row.getValue(key);
        return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
      });
    },
  });

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    
    // If onSearch is provided, call it with the search value
    if (onSearch) {
      onSearch(value);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setGlobalFilter('');
    if (onSearch) {
      onSearch('');
    }
  };

  return (
    <div className="flex flex-col w-full relative">
      {/* Bulk actions toolbar */}
      {enableRowSelection && selectedIds.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
          <div className="text-sm text-gray-700">{selectedIds.length} selected</div>
          <div className="flex items-center gap-2">
            {bulkActions.map((action, index) => (
              <button
                key={index}
                onClick={() => action.onClick(selectedIds, () => setSelectedIds([]))}
                className={`inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${getButtonColorClasses(action.color)}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Table Header with Search */}
      <div className="flex justify-end items-center px-4 py-2">
        <div className="relative w-64">
          <input
            type="text"
            value={globalFilter ?? ''}
            onChange={handleSearchChange}
            placeholder="Search..."
            className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {globalFilter && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {/* Table Section */}
      <div className="overflow-x-auto px-4">
        <table className="min-w-full border-collapse divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 transition">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-2 whitespace-nowrap text-sm text-gray-700"
                  >
                    {cell.column.id === 'image' ? (
                      cell.getValue() ? (
                        <img
                          src={cell.getValue()}
                          alt="Trend"
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <span>No Image</span>
                      )
                    ) : cell.column.id === 'images' ? 
                    <AvatarStack images={cell.getValue()} extraCount={cell.getValue().length - 3} /> : 
                    cell.column.id === 'profilePic' ? (
                      cell.getValue()
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && onPageChange && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
          <div className="flex items-center gap-4 text-sm text-gray-700">
            <span>
              {pagination.total === 0
                ? 'Showing 0 results'
                : `Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} results`}
            </span>
            {onLimitChange && (
              <div className="flex items-center gap-1">
                <span>Rows per page:</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  disabled={loading}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {[10, 25, 50, 100].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className={`px-3 py-1 text-sm font-medium rounded-md ${
                pagination.page <= 1 || loading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                let pageNum;
                if (pagination.pages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.pages - 2) {
                  pageNum = pagination.pages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      pageNum === pagination.page
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    } ${loading ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            {/* Next Button */}
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || loading}
              className={`px-3 py-1 text-sm font-medium rounded-md ${
                pagination.page >= pagination.pages || loading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-600">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactTable;
